import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/AuthContext";

export type ProviderKey = "calendar" | "drive" | "gmail" | "slack";

export type IntegrationStatus = {
  connected: boolean;
  account?: string | null;
  scope?: string | null;
  lastSyncedAt?: string | null;
  shareToTeam?: boolean;   // 캘린더: 내 구글 일정 팀 공유 허용(B 모델)
  error?: string | null;
};

type IntegrationsState = Record<ProviderKey, IntegrationStatus>;

const EMPTY: IntegrationsState = {
  calendar: { connected: false },
  drive: { connected: false },
  gmail: { connected: false },
  slack: { connected: false },
};

type IntegrationsContextValue = {
  status: IntegrationsState;
  loading: boolean;
  refresh: () => Promise<void>;
  isConnected: (provider: ProviderKey) => boolean;
};

const IntegrationsContext = createContext<IntegrationsContextValue | null>(null);

/**
 * 계정 레벨 연동 상태 전역 컨텍스트 (Stage ①).
 * /api/integrations/status에서 provider별 연결 상태를 읽어 CRM 전역에서 소비한다.
 * 토큰 값은 서버에만 — 여기엔 connected/account/scope/lastSynced만 온다.
 */
export function IntegrationsProvider({ children }: { children: React.ReactNode }) {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<IntegrationsState>(EMPTY);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/integrations/status", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setStatus({ ...EMPTY, ...(data.integrations || {}) });
    } catch {
      setStatus(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authStatus === "authenticated") refresh();
    else if (authStatus === "unauthenticated") { setStatus(EMPTY); setLoading(false); }
  }, [authStatus, refresh]);

  const value = useMemo<IntegrationsContextValue>(
    () => ({ status, loading, refresh, isConnected: (p) => !!status[p]?.connected }),
    [status, loading, refresh]
  );

  return <IntegrationsContext.Provider value={value}>{children}</IntegrationsContext.Provider>;
}

export function useIntegrations(): IntegrationsContextValue {
  const ctx = useContext(IntegrationsContext);
  if (!ctx) throw new Error("useIntegrations는 IntegrationsProvider 하위에서만 사용할 수 있습니다.");
  return ctx;
}
